import type { ReactComponentImplementation } from '@a2ui/react/v0_9';
import type { MessageProcessor } from '@a2ui/web_core/v0_9';

/**
 * The processors currently mounted, so a settling action can find the data
 * model that is actually on screen.
 *
 * A result-bound action outlives the render that fired it — the underwriter
 * hand-off polls the draft for up to fifteen seconds — and two things happen
 * inside that window on the FIRST request of a page load:
 *
 *  1. the stream finalizes and the message id changes, so the surface list
 *     unmounts and a NEW instance mounts in its place; and
 *  2. that instance builds its own processor, whose surface carries a new data
 *     model.
 *
 * The captured model then belongs to a tree nobody is rendering. Both models
 * share their `data` object, so the receipt was readable at `/results/root`
 * the whole time and only the signal the card renders from stayed stale —
 * measured in the browser as a card stuck on "Staging on the workspace…" over
 * a workspace that already held the change.
 *
 * A module slot rather than React state, for the same reason `handoffSource`
 * is one on the console side: it has to survive the unmount, and refs are for
 * DOM nodes. The value is looked up at the moment of the write and never
 * replayed, so nothing here can put a stale result onto a later card — the
 * surface id is stable per quote, and every proposal on one quote reuses it.
 */
const mounted: MessageProcessor<ReactComponentImplementation>[] = [];

export function registerProcessor(
  processor: MessageProcessor<ReactComponentImplementation>
): () => void {
  mounted.push(processor);
  return () => {
    const at = mounted.indexOf(processor);
    if (at !== -1) mounted.splice(at, 1);
  };
}

/**
 * The live data model for a surface, or null when the answer is not certain.
 *
 * Certain means EXACTLY ONE mounted processor holds the surface. A surface id
 * is derived from the record on screen (`view-proposal-<quoteId>`), so every
 * proposal on one quote reuses it, and two assistant turns can each hold a
 * surface under that id. Picking the newest would then redirect one turn's
 * receipt onto another turn's card — trading a card that renders nothing for a
 * card that renders the wrong thing, which is worse.
 *
 * The case this exists for is not ambiguous: a keyed remount unmounts the old
 * instance before the new one's effect registers, so during the hand-off only
 * the replacement holds the surface. Anything less clear-cut falls back to the
 * model the action captured, which is what shipped before this existed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- A2UI runtime types
export function peekLiveDataModel(surfaceId: string): any {
  const holders = mounted.filter((p) => p.model.getSurface(surfaceId));
  if (holders.length !== 1) return null;
  return holders[0].model.getSurface(surfaceId)?.dataModel ?? null;
}

/** Test seam. */
export function forgetProcessors(): void {
  mounted.length = 0;
}
